import Component from "@glimmer/component";
import { cached } from "@glimmer/tracking";
import { on } from "@ember/modifier";
import { action } from "@ember/object";
import { service } from "@ember/service";
import { modifier } from "ember-modifier";
import dConcatClass from "discourse/ui-kit/helpers/d-concat-class";
import { i18n } from "discourse-i18n";

const IS_PINNED_CLASS = "is-pinned";

export default class ChatMessageSeparator extends Component {
  @service currentUser;

  track = modifier((element) => {
    const intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (
          entry.isIntersecting &&
          entry.intersectionRatio < 1 &&
          entry.boundingClientRect.y < entry.intersectionRect.y
        ) {
          entry.target.classList.add(IS_PINNED_CLASS);
        } else {
          entry.target.classList.remove(IS_PINNED_CLASS);
        }
      },
      { threshold: [0, 1] }
    );

    intersectionObserver.observe(element);

    return () => {
      intersectionObserver?.disconnect();
    };
  });

  @cached
  get firstMessageOfTheDayAt() {
    const message = this.args.message;

    if (!message.previousMessage) {
      return this.#startOfDay(message.createdAt);
    }

    if (
      !this.#areDatesOnSameDay(
        message.previousMessage.createdAt,
        message.createdAt
      )
    ) {
      return this.#startOfDay(message.createdAt);
    }
  }

  @cached
  get formattedFirstMessageDate() {
    if (this.firstMessageOfTheDayAt) {
      return this.#calendarDate(this.firstMessageOfTheDayAt);
    }
  }

  get isNewestMessage() {
    return this.args.message.id === this.args.message.channel.newestMessage?.id;
  }

  get #timezone() {
    return this.currentUser?.user_option?.timezone || moment.tz.guess();
  }

  @action
  onDateClick() {
    return this.args.fetchMessagesByDate?.(this.firstMessageOfTheDayAt);
  }

  #areDatesOnSameDay(a, b) {
    return moment(a)
      .tz(this.#timezone)
      .isSame(moment(b).tz(this.#timezone), "day");
  }

  #startOfDay(date) {
    return moment(date).tz(this.#timezone).startOf("day").format();
  }

  #calendarDate(date) {
    return moment(date)
      .tz(this.#timezone)
      .calendar(moment().tz(this.#timezone), {
        sameDay: `[${i18n("chat.chat_message_separator.today")}]`,
        lastDay: `[${i18n("chat.chat_message_separator.yesterday")}]`,
        lastWeek: "LL",
        sameElse: "LL",
      });
  }

  <template>
    {{#if this.formattedFirstMessageDate}}
      <div
        class={{dConcatClass
          "chat-message-separator"
          "chat-message-separator-date"
          (if this.isNewestMessage "with-last-visit")
        }}
        data-id={{@message.id}}
        role="button"
        {{on "click" this.onDateClick passive=true}}
      >
        <div class="chat-message-separator__text-container" {{this.track}}>
          <span class="chat-message-separator__text">
            {{this.formattedFirstMessageDate}}

            {{#if this.isNewestMessage}}
              <span class="chat-message-separator__last-visit">
                <span
                  class="chat-message-separator__last-visit-separator"
                >-</span>
                {{i18n "chat.last_visit"}}
              </span>
            {{/if}}
          </span>
        </div>
      </div>

      <div class="chat-message-separator__line-container">
        <div class="chat-message-separator__line"></div>
      </div>
    {{else if this.isNewestMessage}}
      <div
        class="chat-message-separator chat-message-separator-new"
        data-id={{@message.id}}
      >
        <div class="chat-message-separator__text-container">
          <span class="chat-message-separator__text">
            {{i18n "chat.last_visit"}}
          </span>
        </div>

        <div class="chat-message-separator__line-container">
          <div class="chat-message-separator__line"></div>
        </div>
      </div>
    {{/if}}
  </template>
}
